import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
    Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from 'lucide-react';
import { incollaPulito } from '../../utils/incollaPulito';
import '../../styles/EditorReferto.css';

/**
 * EditorReferto — l'editor con cui un medico scrive un referto.
 *
 * L'INSIEME AMMESSO LO IMPONE LO SCHEMA, NON LA BARRA.
 * Una barra senza il tasto «colore» non impedisce di incollare del testo
 * colorato: è il modello del documento che deve rifiutarlo. Qui le
 * estensioni caricate SONO l'insieme ammesso — grassetto, corsivo,
 * sottolineato, elenchi, allineamento, e i titoli di livello 1 e 2 — e
 * ProseMirror un nodo che non è nello schema non lo sa nemmeno
 * rappresentare: incollandolo, sparisce in fase di lettura.
 *
 * Quello che si spegne qui sotto non è nascosto: è tolto dallo schema.
 * `codeBlock`, `blockquote`, `horizontalRule`, `strike` e `code` non sono
 * disponibili nemmeno incollando.
 *
 * NIENTE SCELTA LIBERA DI CARATTERE E DIMENSIONE, e non è una povertà
 * della barra: sul server non c'è un browser, e ogni combinazione va
 * mappata a mano in pdfkit. Una che non è prevista uscirebbe dal PDF
 * diversa da come si vedeva, e se ne accorgerebbe il paziente.
 *
 * IL SERVER RIPULISCE COMUNQUE. Questo editor sta sul computer di chi
 * scrive: una richiesta si può mandare senza passarci.
 */

/* I tre stili nominati. Non è una scelta di carattere: è il ruolo del
   testo, ed è ciò che il FSE porterà con sé quando si convertirà in CDA. */
const STILI = [
    { chiave: 'titolo', etichetta: 'Titolo' },
    { chiave: 'sottotitolo', etichetta: 'Sottotitolo' },
    { chiave: 'corpo', etichetta: 'Corpo' },
];

/**
 * `strumenti` e' una funzione (editor) => nodo, disegnata in fondo alla
 * barra: e' cosi' che la pagina aggiunge il selettore della libreria
 * senza che l'editor sappia cosa sia una libreria. Chi la usa inserisce
 * con editor.chain().focus().insertContent(...): focus() rimette la
 * selezione dov'era prima di toccare il selettore, quindi il testo entra
 * nel punto in cui si stava scrivendo e non in fondo.
 */
const EditorReferto = ({ contenuto, onCambia, soloLettura = false, strumenti = null }) => {
    const editor = useEditor({
        editable: !soloLettura,
        extensions: [
            StarterKit.configure({
                /* Tolti dallo schema, non nascosti dalla barra: un blocco
                   di codice o una citazione incollati da Word non hanno
                   dove andare e diventano paragrafi. */
                codeBlock: false,
                blockquote: false,
                horizontalRule: false,
                strike: false,
                code: false,
                heading: { levels: [1, 2] },
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                alignments: ['left', 'center', 'right', 'justify'],
            }),
        ],
        content: contenuto || { type: 'doc', content: [{ type: 'paragraph' }] },
        editorProps: {
            attributes: { class: 'edr-foglio' },
            /* INCOLLARE DA WORD DEVE RIPULIRE.
               `transformPastedHTML` toglie gli attributi che portano la
               tipografia PRIMA che ProseMirror legga l'HTML: `style`,
               `class`, i tag <font>, le immagini incorporate e i commenti
               condizionali di Word. Quello che sopravvive lo filtra
               comunque lo schema, ma togliere gli `style` qui evita che
               un `<span style="font-weight:bold">` diventi grassetto per
               una strada che la barra non controlla. */
            transformPastedHTML: incollaPulito,
        },
        onUpdate: ({ editor: e }) => { if (onCambia) onCambia(e.getJSON()); },
    });

    /* Il contenuto arriva dopo, quando il referto finisce di caricarsi:
       si riallinea solo se è davvero diverso, altrimenti ogni battuta
       rimetterebbe il cursore all'inizio. */
    useEffect(() => {
        if (!editor || !contenuto) return;
        const attuale = JSON.stringify(editor.getJSON());
        if (attuale !== JSON.stringify(contenuto)) {
            editor.commands.setContent(contenuto, false);
        }
        // eslint-disable-next-line
    }, [editor, contenuto && contenuto.__id]);

    if (!editor) return <div className="edr-attesa">Apertura dell&apos;editor…</div>;

    const stileAttivo = editor.isActive('heading', { level: 1 }) ? 'titolo'
        : editor.isActive('heading', { level: 2 }) ? 'sottotitolo' : 'corpo';

    const applicaStile = (v) => {
        if (v === 'titolo') editor.chain().focus().setHeading({ level: 1 }).run();
        else if (v === 'sottotitolo') editor.chain().focus().setHeading({ level: 2 }).run();
        else editor.chain().focus().setParagraph().run();
    };

    const Tasto = ({ attivo, onClick, titolo, children }) => (
        <button type="button" className={`edr-tasto ${attivo ? 'attivo' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClick} title={titolo} disabled={soloLettura}>
            {children}
        </button>
    );

    return (
        <div className={`edr-riquadro ${soloLettura ? 'edr-bloccato' : ''}`}>
            {!soloLettura && (
                <div className="edr-barra">
                    <select value={stileAttivo} onChange={(e) => applicaStile(e.target.value)}
                            className="edr-stile" title="Stile del paragrafo">
                        {STILI.map((s) => (
                            <option key={s.chiave} value={s.chiave}>{s.etichetta}</option>
                        ))}
                    </select>
                    <span className="edr-separatore" />
                    <Tasto attivo={editor.isActive('bold')} titolo="Grassetto"
                           onClick={() => editor.chain().focus().toggleBold().run()}>
                        <Bold size={15} />
                    </Tasto>
                    <Tasto attivo={editor.isActive('italic')} titolo="Corsivo"
                           onClick={() => editor.chain().focus().toggleItalic().run()}>
                        <Italic size={15} />
                    </Tasto>
                    <Tasto attivo={editor.isActive('underline')} titolo="Sottolineato"
                           onClick={() => editor.chain().focus().toggleUnderline().run()}>
                        <UnderlineIcon size={15} />
                    </Tasto>
                    <span className="edr-separatore" />
                    <Tasto attivo={editor.isActive('bulletList')} titolo="Elenco puntato"
                           onClick={() => editor.chain().focus().toggleBulletList().run()}>
                        <List size={15} />
                    </Tasto>
                    <Tasto attivo={editor.isActive('orderedList')} titolo="Elenco numerato"
                           onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                        <ListOrdered size={15} />
                    </Tasto>
                    <span className="edr-separatore" />
                    {[['left', AlignLeft, 'A sinistra'], ['center', AlignCenter, 'Centrato'],
                      ['right', AlignRight, 'A destra'], ['justify', AlignJustify, 'Giustificato']]
                        .map(([v, Icona, t]) => (
                        <Tasto key={v} attivo={editor.isActive({ textAlign: v })} titolo={t}
                               onClick={() => editor.chain().focus().setTextAlign(v).run()}>
                            <Icona size={15} />
                        </Tasto>
                    ))}
                    {strumenti && <span className="edr-separatore" />}
                    {strumenti && strumenti(editor)}
                </div>
            )}
            <EditorContent editor={editor} />
            {!soloLettura && (
                <p className="edr-nota">
                    Si può formattare quello che vedi nella barra e nient&apos;altro. Non è una
                    limitazione dell&apos;editor: il referto è carta intestata della struttura, e
                    tutto ciò che si può scrivere qui è ciò che la stampa sa riprodurre
                    identico. Il testo incollato da Word viene ripulito.
                </p>
            )}
        </div>
    );
};

export default EditorReferto;
